// Compositing canvas du tag + de l'icône + du logo texte sur une texture unique, appliquée
// au matériau générique "logo_zone" du modèle solution-card.glb (un seul GLB réutilisé pour
// toutes les solutions, seule cette texture change). Portage direct de la logique validée
// dans le prototype tmp/index.html (buildLogoDataURL/drawAtHeight/drawTextCoreAligned).

const CANVAS_WIDTH = 512;
const CANVAS_HEIGHT = 569; // ratio proche du quad "logo_zone" du GLB

const LOGO_LAYOUT = {
	tag: { x: 0.955, y: 0.025, height: 0.08, paddingX: 0.025, align: "right" },
	icon: { x: 0.49, y: 0.28, height: 0.414, align: "center" },
	logo: { x: 0.055, bottom: 0.98, coreHeight: 0.05155, align: "left" },
};

const TAG_BG_COLOR = "#001dfc";
const TAG_TEXT_COLOR = "#ffffff";

const imageCache = new Map();
function loadImage(src) {
	if (!imageCache.has(src)) {
		imageCache.set(
			src,
			new Promise((resolve, reject) => {
				const img = new Image();
				img.crossOrigin = "anonymous";
				img.onload = () => resolve(img);
				img.onerror = reject;
				img.src = src;
			})
		);
	}
	return imageCache.get(src);
}

// Hauteur fixe, largeur proportionnelle (un tag "CRM" et un tag "PRÉVENTION" gardent la même
// hauteur, seule leur largeur varie selon le texte). align définit ce que représente xPct :
// 'left' (bord gauche), 'right' (bord droit), 'center' (centre).
function drawAtHeight(ctx, img, xPct, yPct, heightPct, canvasW, canvasH, align) {
	const h = heightPct * canvasH;
	const w = h * (img.width / img.height);
	let x = xPct * canvasW;
	if (align === "right") x -= w;
	else if (align === "center") x -= w / 2;
	ctx.drawImage(img, x, yPct * canvasH, w, h);
}

// Le tag (nom du terme de taxonomie "solution_type") est un contenu éditorial dynamique, pas un
// asset graphique fourni par le designer : on dessine nous-mêmes le pilule + le texte, plutôt que
// de dépendre d'une image par terme (contrairement au prototype tmp/index.html qui utilisait des
// PNG "tag-xxx.png" figés).
function drawTagPill(ctx, label, xPct, yPct, heightPct, paddingXPct, canvasW, canvasH, align) {
	const h = heightPct * canvasH;
	const paddingX = paddingXPct * canvasW;
	ctx.font = `700 ${Math.round(h * 0.46)}px Nunito, sans-serif`;
	ctx.textBaseline = "middle";
	const textWidth = ctx.measureText(label).width;
	const w = textWidth + paddingX * 2;

	let x = xPct * canvasW;
	if (align === "right") x -= w;
	else if (align === "center") x -= w / 2;
	const y = yPct * canvasH;
	// Même rayon que le composant .tag CSS du site (--btn-border-radius), pas une pilule pleine.
	const radius = h * 0.18;

	ctx.fillStyle = TAG_BG_COLOR;
	ctx.beginPath();
	ctx.roundRect(x, y, w, h, radius);
	ctx.fill();

	ctx.fillStyle = TAG_TEXT_COLOR;
	ctx.fillText(label, x + paddingX, y + h / 2 + h * 0.03);
}

// Un logo texte est recadré serré par son export : on mesure la "masse d'encre" par ligne pour
// isoler le corps de lettre principal (hors jambage/hampe) et caler l'échelle dessus plutôt que
// sur la boîte englobante brute, pour que deux logos texte différents rendent à la même taille visuelle.
const inkCoreCache = new Map();
function measureInkCore(img, trim = 0.06) {
	if (inkCoreCache.has(img.src)) return inkCoreCache.get(img.src);

	const w = img.naturalWidth || img.width;
	const h = img.naturalHeight || img.height;
	const canvas = document.createElement("canvas");
	canvas.width = w;
	canvas.height = h;
	const ctx = canvas.getContext("2d");
	ctx.drawImage(img, 0, 0, w, h);
	const { data } = ctx.getImageData(0, 0, w, h);

	const rowMass = new Array(h).fill(0);
	let total = 0;
	for (let y = 0; y < h; y++) {
		let mass = 0;
		for (let x = 0; x < w; x++) mass += data[(y * w + x) * 4 + 3];
		rowMass[y] = mass;
		total += mass;
	}

	const lowCut = total * trim;
	const highCut = total * (1 - trim);
	let cum = 0,
		top = 0,
		bottom = h - 1;
	for (let y = 0; y < h; y++) {
		cum += rowMass[y];
		if (cum >= lowCut) {
			top = y;
			break;
		}
	}
	cum = 0;
	for (let y = 0; y < h; y++) {
		cum += rowMass[y];
		if (cum >= highCut) {
			bottom = y;
			break;
		}
	}

	const result = { top, bottom, coreHeight: Math.max(1, bottom - top), imgHeight: h };
	inkCoreCache.set(img.src, result);
	return result;
}

// Ancre sur la base des lettres (bas du "corps" mesuré, hors descendante) et met à l'échelle sur
// la hauteur de ce corps — pas sur le bord bas brut de l'image, qui inclut la descendante (g, p,
// q…) et ferait sinon remonter la ligne de base des logos qui en ont une par rapport à ceux qui n'en ont pas.
function drawTextCoreAligned(ctx, img, xPct, bottomPct, coreHeightPct, canvasW, canvasH, align) {
	const { bottom, coreHeight } = measureInkCore(img);
	const scale = (coreHeightPct * canvasH) / coreHeight;
	const w = img.width * scale;
	const h = img.height * scale;
	let x = xPct * canvasW;
	if (align === "right") x -= w;
	else if (align === "center") x -= w / 2;
	const y = bottomPct * canvasH - bottom * scale;
	ctx.drawImage(img, x, y, w, h);
}

// Le canvas composité est identique pour toutes les cartes qui partagent la même solution :
// on ne le génère qu'une fois par combinaison tag/icône/logo, pas une fois par carte.
const canvasCache = new Map();

export function buildSolutionCardCanvas({ tag, icon, logo }) {
	const key = `${tag}|${icon}|${logo}`;
	if (!canvasCache.has(key)) {
		canvasCache.set(
			key,
			(async () => {
				const [, iconImg, logoImg] = await Promise.all([
					document.fonts ? document.fonts.load("700 16px Nunito") : Promise.resolve(),
					loadImage(icon),
					loadImage(logo),
				]);

				const canvas = document.createElement("canvas");
				canvas.width = CANVAS_WIDTH;
				canvas.height = CANVAS_HEIGHT;
				const ctx = canvas.getContext("2d");

				const L = LOGO_LAYOUT;
				drawTagPill(ctx, tag.toUpperCase(), L.tag.x, L.tag.y, L.tag.height, L.tag.paddingX, canvas.width, canvas.height, L.tag.align);
				drawAtHeight(ctx, iconImg, L.icon.x, L.icon.y, L.icon.height, canvas.width, canvas.height, L.icon.align);
				drawTextCoreAligned(ctx, logoImg, L.logo.x, L.logo.bottom, L.logo.coreHeight, canvas.width, canvas.height, L.logo.align);

				return canvas;
			})()
		);
	}
	return canvasCache.get(key);
}
